/**
 * Live per-provider model roster for Settings → IA.
 *
 * Fetched when the section mounts, not at build time: providers add and
 * retire models weekly, and the previous hardcoded pair still offered a model
 * OpenCode Go had already dropped. A failed fetch is not an error state for
 * the user — the server already degrades each provider to its static list, and
 * this hook falls back to the same list if the request itself fails, so the
 * dropdowns always render and a save is never blocked.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  defaultModelForProvider,
  STATIC_AI_MODEL_OPTIONS,
  type AiModelOption,
  type AiProviderModelCatalog,
  type SettingsAiProvider,
} from '@zunelo/shared';
import { settingsClient } from '../../services/clients';

export type AiModelCatalogByProvider = Record<SettingsAiProvider, AiProviderModelCatalog>;

const PROVIDERS: readonly SettingsAiProvider[] = ['opencode-go', 'gemini', 'openai-codex'];

/**
 * Pre-fetch placeholder. Deliberately not `status: 'error'`: the request has
 * not failed yet, and Settings would otherwise paint "no se pudo leer" next
 * to its own "consultando…" hint on every open.
 */
function staticEntry(provider: SettingsAiProvider): AiProviderModelCatalog {
  return {
    provider,
    status: 'ok',
    source: 'static',
    models: STATIC_AI_MODEL_OPTIONS[provider].map((id) => ({ id, label: id })),
    checkedAt: new Date(0).toISOString(),
  };
}

function staticCatalog(): AiModelCatalogByProvider {
  return Object.fromEntries(
    PROVIDERS.map((provider) => [provider, staticEntry(provider)]),
  ) as AiModelCatalogByProvider;
}

function erroredCatalog(): AiModelCatalogByProvider {
  return Object.fromEntries(
    PROVIDERS.map((provider) => [provider, { ...staticEntry(provider), status: 'error' }]),
  ) as AiModelCatalogByProvider;
}

export interface AiModelCatalogState {
  catalog: AiModelCatalogByProvider;
  loading: boolean;
  /** Models for a provider, with `selected` appended when the provider stopped listing it. */
  optionsFor: (provider: SettingsAiProvider, selected?: string) => AiModelOption[];
  /**
   * Model to pin when the user picks a provider: the first one that provider
   * actually lists, falling back to the known default when the roster could
   * not be read.
   */
  defaultModelFor: (provider: SettingsAiProvider) => string;
}

export function useAiModelCatalog(): AiModelCatalogState {
  const [catalog, setCatalog] = useState<AiModelCatalogByProvider>(staticCatalog);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    // Defensive: a client without the method (an older mock, a partial test
    // double) must degrade to the static list, not throw out of the effect.
    if (typeof settingsClient.listAiModels !== 'function') {
      setLoading(false);
      return undefined;
    }
    void settingsClient
      .listAiModels()
      .then(({ data }) => {
        if (!active) return;
        const next = staticCatalog();
        for (const entry of data.providers) {
          if (PROVIDERS.includes(entry.provider)) next[entry.provider] = entry;
        }
        setCatalog(next);
      })
      .catch(() => {
        // The request itself failed: mark it so Settings can say the roster
        // could not be read (instead of implying it is complete).
        if (active) setCatalog(erroredCatalog());
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  return useMemo(
    () => ({
      catalog,
      loading,
      defaultModelFor: (provider) =>
        catalog[provider]?.models[0]?.id ?? defaultModelForProvider(provider),
      optionsFor: (provider, selected) => {
        const models = catalog[provider]?.models ?? [];
        if (!selected || models.some((model) => model.id === selected)) return models;
        // A model the user saved earlier must stay selectable, or opening
        // Settings would silently re-point the task at someone else's model.
        return [...models, { id: selected, label: `${selected} · no listado` }];
      },
    }),
    [catalog, loading],
  );
}
